import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ClipboardModule } from 'ngx-clipboard';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgModule } from '@angular/core';
import { NgxTrimDirectiveModule } from 'ngx-trim-directive';
import { TranslateModule } from '@ngx-translate/core';
import { UIRouterModule } from '@uirouter/angular';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { ScheduledEventsCommonModule } from 'rev-portal/scheduledEvents/common/ScheduledEventsCommon.AngularModule';
import { WebexTeamsModule } from 'rev-portal/scheduledEvents/webex-teams/WebexTeams.Module';
import { ZoomAngularModule } from 'rev-portal/scheduledEvents/zoom/ZoomAngularModule';
import { PollsModule } from 'rev-portal/scheduledEvents/polls/Polls.Module';

import { ButtonsAngularModule } from 'rev-shared/ui/buttons/ButtonsAngular.Module';
import { CheckboxAngularModule } from 'rev-shared/ui/checkbox/CheckboxAngular.Module';
import { CollapsableSectionAngularModule } from 'rev-shared/ui/collapsableSection/CollapsableSectionAngular.Module';
import { DateAngularModule } from 'rev-shared/date/DateAngular.Module';
import { DialogAngularModule } from 'rev-shared/ui/dialog/DialogAngular.Module';
import { DialogRegistrationService } from 'rev-shared/ui/dialog/DialogRegistration.Service';
import { DirectivesAngularModule } from 'rev-shared/util/directives/DirectivesAngular.Module';
import { EmojiPickerModule } from 'rev-shared/ui/emojiPicker/EmojiPicker.Module';
import { FileUploadAngularModule } from 'rev-shared/ui/fileUpload/FileUpload.AngularModule';
import { FormGroupAngularModule } from 'rev-shared/ui/formGroup/FormGroupAngular.Module';
import { ImageSelectorAngularModule } from 'rev-shared/ui/image-selector/ImageSelector.AngularModule';
import { InsightAngularModule } from 'rev-shared/ui/insight/InsightAngular.Module';
import { InsightModule } from 'rev-shared/ui/insight/ngx/Insight.Module';
import { LiveSubtitlesModule } from 'rev-shared/ui/liveSubtitles/LiveSubtitles.Module';
import { SelectAngularModule } from 'rev-shared/ui/select/SelectAngular.Module';
import { SpinnerAngularModule } from 'rev-shared/ui/spinner/SpinnerAngular.Module';
import { TemplatesAngularModule } from 'rev-shared/ui/templates/TemplatesAngular.Module';
import { TemplatesMenuAngularModule } from 'rev-shared/ui/templates/TemplatesMenuAngular.Module';
import { TranscriptionModule } from 'rev-shared/media/transcription/Transcription.Module';
import { ValidationAngularModule } from 'rev-shared/ui/validation/ValidationAngular.Module';
import { ValidationModule } from 'rev-shared/util/directives/form/validation/Validation.AngularModule';
import { VbDateTimePickerModule } from 'rev-shared/ui/dateTimePicker/VbDateTimePicker.Module';
import { VbProfilePictureModule } from 'rev-shared/ui/profile/VbProfilePicture.Module';
import { VbTagsInputModule } from 'rev-shared/ui/ngTagsInput/Ngx/VbTagsInput.Module';
import { VbTextAreaModule } from 'rev-shared/ui/textArea/VbTextArea.Module';
import { VbUiColorSelectorModule } from 'rev-shared/ui/color-selector/VbUiColorSelector.Module';
import { VbUiDurationInputAngularModule } from 'rev-shared/ui/durationInput/VbUiDurationInputAngular.Module';
import { VbUiHtmlEditorComponent } from 'rev-shared/htmlEditor/VbUiHtmlEditor.Component';
import { VbUiSplitLinesInputModule } from 'rev-shared/ui/splitLinesInput/VbUiSplitLinesInput.Module';
import { VbUiToolbarModule } from 'rev-shared/ui/vbUiToolbar/VbUiToolbarAngular.Module';
import { VbUiTooltipModule } from 'rev-shared/ui/tooltip/VbUiTooltip.Module';
import { VirtualScrollTypeaheadAngularModule } from 'rev-shared/ui/virtualScrollTypeahead/VirtualScrollTypeahead.AngularModule';

import { AvailabilityIconCellRendererComponent } from './AvailabilityIconCellRenderer.Component';
import { DataGridModule } from 'rev-shared/ui/dataGrid/DataGrid.Module';
import { EditExternalPresentersComponent } from './EditExternalPresenters.Component';
import { EditSpeakerProfilesComponent } from './EditSpeakerProfiles.Component';
import { EditWebcastAttendeeSettingsComponent } from './EditWebcastAttendeeSettings.Component';
import { EditWebcastBannerDetailsComponent } from './EditWebcastBannerDetails.Component';
import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { EditWebcastBasicSettingsComponent } from './EditWebcastBasicSettings.Component';
import { EditWebcastBrandingComponent } from './EditWebcastBranding.Component';
import { EditWebcastComponent } from './EditWebcast.Component';
import { EditWebcastCustomFieldsComponent } from './EditWebcastCustomFields.Component';
import { EditWebcastEmbeddedContentComponent } from './EditWebcastEmbeddedContent.Component';
import { EditWebcastEngagementSettingsComponent } from './EditWebcastEngagementSettings.Component';
import { EditWebcastFormComponent } from './EditWebcastForm.Component';
import { EditWebcastHostsSettingsComponent } from './EditWebcastHostsSettings.Component';
import { EditWebcastProducerSettingsComponent } from './EditWebcastProducerSettings.Component';
import { EditWebcastRecordingSettingsComponent } from './EditWebcastRecordingSettingsComponent';
import { EditWebcastVideoSettingsComponent } from './EditWebcastVideoSettings.Component';
import { EditWebcastVideoSettingsSidebarComponent } from './EditWebcastVideoSettingsSidebar.Component';
import { PresentationProfileGridInputComponent } from './PresentationProfileGridInput.Component';
import { PresentationProfilePickerTableComponent } from './PresentationProfilePickerTable.Component';
import { ResourceScheduleDialogComponent } from './ResourceScheduleDialog.Component';
import { RestartWebcastSourceDialogComponent } from './RestartWebcastSourceDialog.Component';
import { RtmpVideoSourceDetailsComponent } from './RtmpVideoSourceDetails.Component';
import { SelectRegistrationFieldsComponent } from './registrationFields/SelectRegistrationFields.Component';
import { ShortcutNameAsyncValidatorDirective } from './ShortcutNameAsyncValidator.Directive';
import { SortableSelectionGridComponent } from './registrationFields/SortableSelectionGrid.Component';

const declarations = [
	AvailabilityIconCellRendererComponent,
	EditExternalPresentersComponent,
	EditSpeakerProfilesComponent,
	EditWebcastAttendeeSettingsComponent,
	EditWebcastBannerDetailsComponent,
	EditWebcastBaseSettingsComponent,
	EditWebcastBasicSettingsComponent,
	EditWebcastBrandingComponent,
	EditWebcastComponent,
	EditWebcastCustomFieldsComponent,
	EditWebcastEmbeddedContentComponent,
	EditWebcastEngagementSettingsComponent,
	EditWebcastFormComponent,
	EditWebcastHostsSettingsComponent,
	EditWebcastProducerSettingsComponent,
	EditWebcastRecordingSettingsComponent,
	EditWebcastVideoSettingsComponent,
	EditWebcastVideoSettingsSidebarComponent,
	PresentationProfileGridInputComponent,
	PresentationProfilePickerTableComponent,
	ResourceScheduleDialogComponent,
	RestartWebcastSourceDialogComponent,
	RtmpVideoSourceDetailsComponent,
	SelectRegistrationFieldsComponent,
	ShortcutNameAsyncValidatorDirective,
	SortableSelectionGridComponent
];

@NgModule({
	declarations,
	exports: [
		EditWebcastComponent
	],
	imports: [
		BsDropdownModule,
		ButtonsAngularModule,
		CheckboxAngularModule,
		DataGridModule,
		ClipboardModule,
		CollapsableSectionAngularModule,
		CommonModule,
		DateAngularModule,
		DialogAngularModule,
		DirectivesAngularModule,
		DragDropModule,
		EmojiPickerModule,
		FileUploadAngularModule,
		FormGroupAngularModule,
		FormsModule,
		ImageSelectorAngularModule,
		InsightAngularModule,
		InsightModule,
		LiveSubtitlesModule,
		NgxTrimDirectiveModule,
		PollsModule,
		ReactiveFormsModule,
		ScheduledEventsCommonModule,
		SelectAngularModule,
		SpinnerAngularModule,
		TemplatesAngularModule,
		TemplatesMenuAngularModule,
		TooltipModule,
		TranscriptionModule,
		TranslateModule,
		TypeaheadModule,
		UIRouterModule,
		ValidationAngularModule,
		ValidationModule,
		VbDateTimePickerModule,
		VbProfilePictureModule,
		VbTagsInputModule,
		VbTextAreaModule,
		VbUiColorSelectorModule,
		VbUiDurationInputAngularModule,
		VbUiHtmlEditorComponent,
		VbUiSplitLinesInputModule,
		VbUiToolbarModule,
		VbUiTooltipModule,
		VirtualScrollTypeaheadAngularModule,
		WebexTeamsModule,
		ZoomAngularModule
	]
})
export class EditWebcastAngularModule {
	constructor(dialog: DialogRegistrationService) {
		dialog.register('ShortcutSchedule', {
			component: ResourceScheduleDialogComponent
		});

		dialog.register('EventVideoSourceSchedule', {
			component: ResourceScheduleDialogComponent
		});
		dialog.register('RestartWebcastSource', {
			component: RestartWebcastSourceDialogComponent
		});
	}
}
